// Copyright (c) 2020, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { Query, Mutation } from 'react-apollo'
import { withRouter } from 'react-router-dom'
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import { faSms } from '@fortawesome/free-solid-svg-icons'
import { faTelegram } from '@fortawesome/free-brands-svg-icons'
import moment from 'moment'
import { RadialChart } from 'react-vis'
import DatePicker from 'react-datepicker'

import {
  EmployerBulkMessageSMSMessages,
  EmployerBulkMessageTelegramMessages,
  Card,
  Grid,
  LabelledDetail as Detail,
  SpanWithData,
  Spinner,
  Warning,
  SvgIcon,
  Button,
  NavLink,
  TabbedView
} from '../../components'

import {
  CHANNEL_SMS_MESSAGEPLUS,
  CHANNEL_TELEGRAM_BROADCAST
} from '../../constants'

import { QUERY_BULKMESSAGE } from '../../queries'
import { MUTATION_BULKMESSAGE } from '../../mutations'


class EmployerBulkMessage extends Component {
  static propTypes = {
    fromAddr: PropTypes.string,
    toAddr: PropTypes.string,
    title: PropTypes.string,
    noStatusFilter: PropTypes.bool
  }

  static defaultProps = {
    className: ''
  }

  state = {
    openTab: 0,
    inProgressReadyAndGenerate: false,
    errorReadyAndGenerate: false,
    messagesRefresh: null,
    editScheduledAt: false
  }

  componentDidUpdate(prevProps, prevState) {
    const prevEmployerId = prevProps.match.params.employerId
    const currentEmployerId = this.props.match.params.employerId
    const prevEmployerGroupId = prevProps.match.params.employerGroupId
    const currentEmployerGroupId = this.props.match.params.employerGroupId

    if (currentEmployerId && prevEmployerId !== currentEmployerId) {  // switching between employer accounts
      this.props.history.push(`/employer/${currentEmployerId}/communications/bulk?tab=1`)
    }
    else if (currentEmployerId && prevEmployerGroupId) {  // switching from group to specific employer account
      this.props.history.push(`/employer/${currentEmployerId}/communications/bulk?tab=1`)
    }
    else if (currentEmployerGroupId && prevEmployerGroupId !== currentEmployerGroupId) {  // switching between employer group accounts
      this.props.history.push(`/group/${currentEmployerGroupId}/communications/bulk?tab=1`)
    }
    else if (currentEmployerGroupId && prevEmployerId) {  // switching from specifc to employer group account
      this.props.history.push(`/group/${currentEmployerGroupId}/communications/bulk?tab=1`)
    }
  }

  readyBulkMessageAndGenerateMessages = (bulkmessageMutate, refetchBulkMessageStatus) => {
    this.setState(
      { inProgressReadyAndGenerate: true },
      () => {
        bulkmessageMutate({
          variables: {
            input: {
              id: this.props.match.params.messageId,
              status: 'ready'
            }
          }
        })
        .then(() => (
          setTimeout(
            () => (
              this.setState(
                {
                  inProgressReadyAndGenerate: false,
                  errorReadyAndGenerate: false
                },
                () => {
                  refetchBulkMessageStatus()
                  this.refreshMessages()
                }
              )
            ),
            1000  // 1 sec
          )
        ))
        .catch(() => (
          this.setState({
            inProgressReadyAndGenerate: false,
            errorReadyAndGenerate: true
          })
        ))
      }
    )
  }

  updateScheduleAt = (bulkmessageMutate, newScheduledAtDate, refetch) => {
    bulkmessageMutate({
      variables: {
        input: {
          id: this.props.match.params.messageId,
          scheduledAt: newScheduledAtDate
        }
      }
    })
    .then(() => {
      refetch()
      this.toggleEditScheduledAt()
    })
  }

  refreshMessages = () => (
    this.setState({
      messagesRefresh: moment().unix()  // get unix timestamp in seconds
    })
  )

  cancelScheduledBulkMessage = (bulkmessageMutate) => {
    bulkmessageMutate({
      variables: {
        input: {
          id: this.props.match.params.messageId,
          status: 'cancelled'
        }
      }
    })
  }

  resendFailedMessages = (bulkmessageMutate) => {
    // perform simple save to trigger resend of failed messages
    bulkmessageMutate({
      variables: {
        input: {
          id: this.props.match.params.messageId
        }
      }
    })
    .then(this.refreshMessages)
  }

  toggleEditScheduledAt = () => (
    this.setState(prevState => ({ editScheduledAt: !prevState.editScheduledAt }))
  )

  expectedMessagesGenerated = (channels, totalSmsMessages, totalTelegramMessages) => {
    if (channels.edges.length === 1) {
      if (channels.edges[0].node.id === CHANNEL_SMS_MESSAGEPLUS) {
        return totalSmsMessages > 0
      }
      else if (channels.edges[0].node.id === CHANNEL_TELEGRAM_BROADCAST) {
        return totalTelegramMessages > 0
      }
    }

    if (channels.edges.length === 2) {
      return totalSmsMessages > 0 && totalTelegramMessages > 0
    }

    return false
  }

  render() {
    return (
      <Query query={QUERY_BULKMESSAGE} variables={{ id: this.props.match.params.messageId }}>
        {({ loading, error, data, refetch }) => {
          if (loading) {
            return <Spinner type='brand' />
          }

          if (error) {
            return <Warning>Failed to fetch message details</Warning>
          }

          const message = data.bulkmessage

          const totalSmsMessages = data.bulkmessage.messages.totalCount
          const sentSmsMessages = data.bulkmessage.messages.edges.filter(({ node: message }) => (
            message.status === 'SENT_AAT'
          )).length
          const unsentSmsMessages = totalSmsMessages - sentSmsMessages

          const totalTelegramMessages = data.bulkmessage.telegrammessages.totalCount
          const sentTelegramMessages = data.bulkmessage.telegrammessages.edges.filter(({ node: message }) => (
            message.status === 'SENT_UPSTREAM'
          )).length
          const unsentTelegramMessages = totalTelegramMessages - sentTelegramMessages

          let messageListings = []
          let messageListingContents = []

          if (data.bulkmessage.channels.edges.length > 1) {
            messageListings = ['SMS Messages', 'Telegram Messages']
            messageListingContents = [
              <Mutation mutation={MUTATION_BULKMESSAGE}>
                {bulkmessageMutate => (
                  <EmployerBulkMessageSMSMessages
                    bulkMessageId={this.props.match.params.messageId}
                    numberOfFailedMessages={message.numberOfFailedMessages}
                    refresh={this.refreshMessages}
                    resendFailedMessages={() => this.resendFailedMessages(bulkmessageMutate)}
                    key={this.state.messagesRefresh}
                  />
                )}
              </Mutation>,
              <Mutation mutation={MUTATION_BULKMESSAGE}>
                {bulkmessageMutate => (
                  <EmployerBulkMessageTelegramMessages
                    bulkMessageId={this.props.match.params.messageId}
                    numberOfFailedMessages={message.numberOfFailedMessages}
                    refresh={this.refreshMessages}
                    resendFailedMessages={() => this.resendFailedMessages(bulkmessageMutate)}
                    key={this.state.messagesRefresh}
                  />
                )}
              </Mutation>
            ]
          }
          else if (data.bulkmessage.channels.edges.length === 1) {
            if (data.bulkmessage.channels.edges[0].node.id === CHANNEL_SMS_MESSAGEPLUS) {
              messageListings.push('SMS Messages')
              messageListingContents.push(
                <Mutation mutation={MUTATION_BULKMESSAGE}>
                  {bulkmessageMutate => (
                    <EmployerBulkMessageSMSMessages
                      bulkMessageId={this.props.match.params.messageId}
                      numberOfFailedMessages={message.numberOfFailedMessages}
                      refresh={this.refreshMessages}
                      resendFailedMessages={() => this.resendFailedMessages(bulkmessageMutate)}
                      key={this.state.messagesRefresh}
                    />
                  )}
                </Mutation>
              )
            }
            else {
              messageListings.push('Telegram Messages')
              messageListingContents.push(
                <Mutation mutation={MUTATION_BULKMESSAGE}>
                  {bulkmessageMutate => (
                    <EmployerBulkMessageTelegramMessages
                      bulkMessageId={this.props.match.params.messageId}
                      numberOfFailedMessages={message.numberOfFailedMessages}
                      refresh={this.refreshMessages}
                      resendFailedMessages={() => this.resendFailedMessages(bulkmessageMutate)}
                      key={this.state.messagesRefresh}
                    />
                  )}
                </Mutation>
              )
            }
          }

          const details = [
            <Detail
              label='Status'
              detail={<SpanWithData forField='BulkMessageStatus' searchKey={message.status} />}
            />,
            <Detail
              label='Audience'
              detail={
                message.audience.archived
                  ? message.audience.name
                  : <NavLink to={`/employer/${this.props.match.params.employerId}/communications/bulk/audience/${message.audience.id}`}>
                      {message.audience.name}
                    </NavLink>
              }
            />,
            <Detail
              label='Created at'
              detail={moment(message.createdAt).format('lll')}
            />,
            <Detail
              label={
                <div>
                  Scheduled at
                  {this.state.editScheduledAt &&
                    <SvgIcon
                      title='close date picker'
                      variant='utility'
                      symbol='close'
                      size='x-small'
                      colour='success'
                      className='clickable slds-float_right'
                      onClick={this.toggleEditScheduledAt}
                    />
                  }
                </div>
              }
              detail={message.scheduledAt
                ? this.state.editScheduledAt
                  ? <Mutation mutation={MUTATION_BULKMESSAGE}>
                      {bulkmessageMutate => (
                        <div style={{ width: '110%' }}>
                          <DatePicker
                            selected={moment(message.scheduledAt)}
                            onChange={scheduledAt => this.updateScheduleAt(bulkmessageMutate, scheduledAt, refetch)}
                            dateFormat='D MMM YYYY'
                            showTimeSelect={true}
                            timeFormat='HH:mm'
                            minDate={moment()}
                            maxDate={moment().add(60, 'days')}
                            inline={true}
                            className='slds-m-top_small'
                          />
                        </div>
                      )}
                    </Mutation>
                  : <div>
                      {moment(message.scheduledAt).format('lll')}
                      <SvgIcon
                        title='edit'
                        variant='utility'
                        symbol='edit'
                        size='x-small'
                        colour='success'
                        className='slds-float_right clickable'
                        onClick={this.toggleEditScheduledAt}
                      />
                    </div>
                : '-'
              }
            />
          ]

          return (
            <div className={`slds-size_1-of-1 ${this.props.className}`}>
              {/* product overview section */}
              <div className='slds-p-top_small slds-p-left_large slds-p-right_large slds-border_bottom'>
                <div className='slds-grid'>
                  <Card
                    title={
                      <div>
                        Bulk Message
                        {message.scheduledAt &&
                          moment(message.scheduledAt).isAfter(moment()) &&
                          message.status !== 'CANCELLED' &&
                          <Mutation mutation={MUTATION_BULKMESSAGE}>
                            {bulkmessageMutate => (
                              <Button
                                title='Cancel sending of scheduled bulk message'
                                onClick={() => this.cancelScheduledBulkMessage(bulkmessageMutate)}
                                className='u-font-weight-normal slds-float_right'>
                                Cancel
                              </Button>
                            )}
                          </Mutation>
                        }
                      </div>
                    }
                    body={
                      <div>
                        <Grid
                          items={[
                            <Detail
                              label='Description'
                              detail={message.description}
                              detailFontSize="large"
                              detailFontWeight="bold"
                              className='slds-p-bottom_small'
                            />,
                            <Detail
                              label='Content'
                              detail={
                                <div
                                  className='slds-p-around_small slds-m-top_small'
                                  style={{
                                    wordWrap: 'break-word',
                                    workBreak: 'break-word',
                                    whiteSpace: 'pre-line',
                                    letterSpacing: '0.75px',
                                    fontSize: '0.75em',
                                    border: '1px solid grey',
                                    borderRadius: '5px',
                                    backgroundColor: '#DDECCF'
                                  }}>
                                  {message.content}
                                </div>
                              }
                              detailFontSize="large"
                              detailFontWeight="bolder"
                              className='slds-p-bottom_small'
                            />
                          ]}
                        />
                        <Grid
                          items={details}
                          className='slds-m-top_medium slds-m-bottom_medium'
                          itemClassName='slds-m-top_small slds-m-bottom_x-large slds-p-bottom_small slds-border_bottom'
                        />
                      </div>
                    }
                    paddingTRL={true}
                    className='slds-size_1-of-2'
                  />
                  <div className='slds-size_1-of-2 slds-m-left_medium slds-p-around_large u-bg-color-white'>
                    {message.audience.identities.totalCount === 0
                      ? <Warning>
                          <span className='slds-p-right_small'>
                            Audience has no members.
                          </span>
                          <NavLink to={`/employer/${this.props.match.params.employerId}/communications/bulk/audience/${message.audience.id}`}>
                            Add?
                          </NavLink>
                        </Warning>
                      : !this.expectedMessagesGenerated(message.channels, totalSmsMessages, totalTelegramMessages)
                        ? <div className='u-font-size-larger slds-grid' style={{ alignItems: 'center' }}>
                            {this.state.errorReadyAndGenerate
                              ? <Warning>Failed to finalise.</Warning>
                              : <Fragment>
                                  <SvgIcon
                                    variant='utility'
                                    symbol='info'
                                    colour='light'
                                    size='small'
                                    className='slds-p-right_x-small'
                                  />
                                  No messages generated yet.
                                </Fragment>
                            }
                            <Mutation mutation={MUTATION_BULKMESSAGE}>
                              {bulkmessageMutate => (
                                <Button
                                  type='reset'
                                  onClick={() => this.readyBulkMessageAndGenerateMessages(bulkmessageMutate, refetch)}
                                  // disabled={this.state.inProgressReadyAndGenerate}
                                  minWidth={this.state.inProgressReadyAndGenerate
                                    ? 50
                                    : null
                                  }
                                  className='slds-p-left_small'>
                                  {this.state.inProgressReadyAndGenerate
                                    ? <Spinner type='brand' size='x-small' className='slds-p-left_medium'/>
                                    : this.state.errorReadyAndGenerate
                                      ? 'Try again?'
                                      : 'Finalise & generate?'
                                  }
                                </Button>
                              )}
                            </Mutation>
                          </div>
                        : <div className='slds-grid slds-size_1-of-1' style={{ justifyContent: 'space-around' }}>
                            {totalSmsMessages > 0 &&
                              <div className='bulkMessageRadialChartContainer slds-grid slds-size_1-of-2'>
                                <div className='bulkMessageRadialChart'>
                                  <RadialChart
                                    data={sentSmsMessages < unsentSmsMessages
                                      ? [
                                          {
                                            angle: sentSmsMessages,
                                            label: sentSmsMessages > 0
                                              ? 'Sent'
                                              : ''
                                          },
                                          {
                                            angle: totalSmsMessages - sentSmsMessages,
                                            label: sentSmsMessages === 0
                                              ? 'No messages sent'
                                              : ''
                                          }
                                        ]
                                      : [
                                          {
                                            angle: totalSmsMessages - unsentSmsMessages,
                                            label: totalSmsMessages - sentSmsMessages === 0
                                              ? 'All messages sent'
                                              : ''
                                            },
                                          {
                                            angle: unsentSmsMessages,
                                            label: unsentSmsMessages > 0
                                              ? 'Unsent'
                                              : ''
                                            }
                                        ]
                                    }
                                    width={300}
                                    height={300}
                                    innerRadius={100}
                                    showLabels={true}
                                    // labelsRadiusMultiplier={1.2}
                                  />
                                  <Stats
                                    totalMessages={totalSmsMessages}
                                    sentMessages={sentSmsMessages}
                                    unsentMessages={unsentSmsMessages}
                                    numberOfRecipients={message.audience.identities.totalCount}
                                  />
                                </div>
                                <div className='bulkMessageRadialChartIcon'>
                                  <Icon icon={faSms} size='3x' className='slds-m-right_x-small' />
                                </div>
                              </div>
                            }
                            {totalTelegramMessages > 0 &&
                              <div className='bulkMessageRadialChartContainer slds-grid slds-size_1-of-2'>
                                <div className='bulkMessageRadialChart'>
                                  <RadialChart
                                    data={sentTelegramMessages < unsentTelegramMessages
                                      ? [
                                          {
                                            angle: sentTelegramMessages,
                                            label: sentTelegramMessages > 0
                                              ? 'Sent'
                                              : ''
                                          },
                                          {
                                            angle: totalTelegramMessages - sentTelegramMessages,
                                            label: sentTelegramMessages === 0
                                              ? 'No messages sent'
                                              : ''
                                          }
                                        ]
                                      : [
                                          {
                                            angle: totalTelegramMessages - unsentTelegramMessages,
                                            label: totalTelegramMessages - sentTelegramMessages === 0
                                              ? 'All messages sent'
                                              : ''
                                            },
                                          {
                                            angle: unsentTelegramMessages,
                                            label: unsentTelegramMessages > 0
                                              ? 'Unsent'
                                              : ''
                                            }
                                        ]
                                    }
                                    width={300}
                                    height={300}
                                    innerRadius={100}
                                    showLabels={true}
                                    // labelsRadiusMultiplier={1.1}
                                  />
                                  <Stats
                                    totalMessages={totalTelegramMessages}
                                    sentMessages={sentTelegramMessages}
                                    unsentMessages={unsentTelegramMessages}
                                    numberOfRecipients={message.audience.identities.totalCount}
                                  />
                                </div>
                                <div className='bulkMessageRadialChartIcon'>
                                  <Icon icon={faTelegram} size='3x' className='slds-m-right_x-small' />
                                </div>
                              </div>
                            }
                          </div>
                    }
                  </div>
                </div>
              </div>
              <TabbedView
                tabs={messageListings}
                contents={messageListingContents}
                tabSpacing='x-large'
                selectedTab={this.state.openTab}
                onClick={tabIndex => this.setState({ openTab: tabIndex })}
                tabsClassName='slds-p-left_large slds-p-right_large'
              />
            </div>
          )
        }}
      </Query>
    )
  }
}

export default withRouter(EmployerBulkMessage)


const Stats = ({ totalMessages, sentMessages, unsentMessages, numberOfRecipients }) => (
  <div className='slds-m-top_x-large slds-p-left_small' style={{ lineHeight: 2, borderLeft: '3px solid lightblue' }}>
    {`${totalMessages} SMS messages to ${numberOfRecipients} contacts`}
    <br />
    {`${sentMessages} sent vs ${unsentMessages} unsent`}
  </div>
)



// WEBPACK FOOTER //
// ./src/containers/messageplus/EmployerBulkMessage.js